import cn from 'classnames';
import { FormattedMessage } from 'react-intl';
import { useIntl } from 'react-intl/lib';

import { FeatureFlag } from '@/shared/api/protocol-ts/model/dto_organization_pb';
import { useAppSelector } from '@/shared/hooks';
import { trackEvent } from '@/shared/lib';
import { Icon, Switch, Tooltip } from '@/shared/ui';

import { organizationModel } from '@/entities/organization';
import { useCheckReportSignature, useReportSettings } from '@/entities/reports';

import { useFDA2DRestrictions } from '@/features/fdaRestrictions';

import styles from './Report2DSettingsBar.module.scss';

type Report2DSettingsBarProps = {
  className?: string;
};

export const Report2DSettingsBar = (props: Report2DSettingsBarProps) => {
  const { className } = props;

  const { formatMessage } = useIntl();

  const { isLoading, settings, updateSettings } = useReportSettings();

  const hideProbability = useAppSelector(
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Hide_Probabilities,
    ),
  );

  const { shouldHideSuspiciousTeethToggle, shouldHideConditionDetailsToggle } =
    useFDA2DRestrictions();

  const { checkReportSignature } = useCheckReportSignature();

  const isLowProbabilityModeEnabled = settings.LowProbabilityMode;
  const isShowConditionDetailsEnabled = settings.ShowConditionDetails;

  const lowProbabilityModeToggle = () => {
    if (!isLowProbabilityModeEnabled) {
      trackEvent({
        eventName: 'ReportPageFooterSuspiciousTeethToggleClicked',
      });
    }

    checkReportSignature({
      onSignatureChecked: async () =>
        updateSettings({ LowProbabilityMode: !isLowProbabilityModeEnabled }),
    });
  };

  const toggleConditionDetails = () => {
    if (!isShowConditionDetailsEnabled) {
      trackEvent({
        eventName: 'ReportPageFooterConditionDetailsToggleClicked',
      });
    }

    checkReportSignature({
      onSignatureChecked: async () =>
        updateSettings({
          ShowConditionDetails: !isShowConditionDetailsEnabled,
        }),
    });
  };

  return (
    <div
      className={cn(styles.container, className)}
      data-component="report-2d-settings"
    >
      {!hideProbability && !shouldHideSuspiciousTeethToggle && (
        <div className={styles.lowProbability}>
          <Switch
            labelClassName="p3"
            checked={isLowProbabilityModeEnabled}
            disabled={isLoading}
            onChange={lowProbabilityModeToggle}
          >
            <FormattedMessage
              id="reportSettings.showLowProbability"
              defaultMessage="Suspicious teeth"
            />
          </Switch>

          <Tooltip.Primary
            content={formatMessage({
              id: 'lowProbabilityInfo',
              defaultMessage:
                'Switch on to see caries signs and periradicular pathologies, with probability rate 30-50%',
            })}
          >
            <div className={styles.lowProbabilityInfoIcon}>
              <Icon name="info" size={24} />
            </div>
          </Tooltip.Primary>
        </div>
      )}

      {!shouldHideConditionDetailsToggle && (
        <div className={styles.diagnosisDetails}>
          <Switch
            className={styles.toggle}
            labelClassName="p3"
            checked={isShowConditionDetailsEnabled}
            disabled={isLoading}
            onChange={toggleConditionDetails}
          >
            <FormattedMessage
              id="reportSettings.showConditionDetails"
              defaultMessage="Conditions details"
            />
          </Switch>

          <Tooltip.Primary
            content={formatMessage({
              id: 'showConditionDetailsInfo',
              defaultMessage:
                'Turn on to view all detected conditions with details',
            })}
          >
            <div className={styles.lowProbabilityInfoIcon}>
              <Icon name="info" size={24} />
            </div>
          </Tooltip.Primary>
        </div>
      )}
    </div>
  );
};
